import adminService from '../services/admin'

// State
export const state = {
  fileUpload: '',
  totalStockProducts: 450,
  stockProducts: [],
  imageryList: [],
  managementImageryList: [],
  imageryListData: {},
  managementImageryColumn: [],
  managementImageryData: {},
  preloadCache: {}
}

// getters
export const getters = {
  fileUpload: state => state.fileUpload,
  stockProducts: state => state.stockProducts,
  totalStockProducts: state => state.totalStockProducts,
  adminImageryList: state => state.imageryList,
  adminManagementImageryList: state => state.managementImageryList,
  imageryListData: state => state.imageryListData,
  managementColumn: state => state.managementImageryColumn,
  managementImageryData: state => state.managementImageryData,
  preloadCache: state => state.preloadCache
}

export const mutations = {
  UPDATE_STOCK_PRODUCTS (state, { data }) {
    state.stockProducts = data
  },
  GET_ADMIN_IMAGERY (state, data) {
    state.imageryList = data?.data?.map(image => ({ ...image, checked: false }))
    state.imageryListData = data
  },
  GET_ADMIN_MANAGEMENT_IMAGERY (state, data) {
    const allImagesWithChecked = Object.values(data?.data)
      .map(image => ({ ...image, checked: false }))
    state.managementImageryList = allImagesWithChecked
    // Fixed 6-slot grid: every product row shows Main + Image 2..6, each independently
    // uploadable. Columns no longer derive from the page's data (which hid no-image
    // products and could push Main out of column 1).
    state.managementImageryColumn = ['Main Image', 'Image 2', 'Image 3', 'Image 4', 'Image 5', 'Image 6']
    state.managementImageryData = data
  },
  RESET_IMAGE_MANAGEMENT_IMAGERY (state) {
    state.managementImageryList = []
    state.managementImageryColumn = []
    state.managementImageryData = {}
  },
  SET_PRELOAD_CACHE (state, { cacheKey, data }) {
    state.preloadCache[cacheKey] = data
  },
  CLEAR_PRELOAD_CACHE (state) {
    state.preloadCache = {}
  },
  CLEANUP_PRELOAD_CACHE (state, maxEntries = 5) {
    const cacheKeys = Object.keys(state.preloadCache)
    if (cacheKeys.length > maxEntries) {
      const keysToRemove = cacheKeys.slice(0, cacheKeys.length - maxEntries)
      keysToRemove.forEach(key => {
        delete state.preloadCache[key]
      })
    }
  }

}

// actions
export const actions = {
  async upload ({ commit }, payload) {
    try {
      const { data: { message } } = await adminService.fileUpload(payload)
      return message
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async exportFile ({ commit }, { filter, payload }) {
    try {
      const { data: { message } } = await adminService.exportFile(filter, payload)
      return message
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async fetchColumns ({ commit }) {
    try {
      return await adminService.fetchColumns()
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async updateStockColumn ({ commit }, payload) {
    try {
      return await adminService.updateStockColumn(payload)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async updateStockColumnOrder ({ commit }, payload) {
    try {
      return await adminService.updateStockColumnOrder(payload)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async fetchStock ({ commit }, { search, sort_by, sort_order, page, limit, filter_criteria, logical_operators }) {
    try {
      const response = await adminService.fetchStock(search, sort_by, sort_order, page, limit, filter_criteria, logical_operators)
      commit('UPDATE_STOCK_PRODUCTS', response?.data?.data)
      return response
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async bulkUpdate ({ commit }, payload) {
    try {
      return await adminService.bulkUpdate(payload)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  // 422s are shown field by field by the drawer and the interceptor owns the rest, so the
  // rejection is left to propagate untouched.
  async createProduct ({ commit }, payload) {
    return adminService.createProduct(payload)
  },
  // The rejection propagates: the dialog reports the failure and keeps the product on screen.
  async deleteProduct ({ commit }, id) {
    return adminService.deleteProduct(id)
  },
  async bulkDelete({ commit }, selectedItems) {
    try {
        return await adminService.bulkDelete(selectedItems)
    } catch (error) {
        console.log('Error: ', error)
        throw error
    }
  },
  async createFilter ({ commit }, payload) {
    try {
      const { data } = await adminService.createFilter(payload)
      return data
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },

  async fetchFilter ({ commit }) {
    try {
      return await adminService.fetchFilter()
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async updateFilter ({ commit }, { id, payload }) {
    try {
        const response = await adminService.updateFilter(id, payload)
        return response // Return the full response instead of just data
    } catch (error) {
        console.log('Error: ', error)
        throw error
    }
  },
  async showFilter ({ commit }, id) {
    try {
      return await adminService.showFilter(id)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async deleteFilter ({ commit }, payload) {
    try {
      return await adminService.deleteFilter(payload)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async updateReorderFilter ({ commit }, payload) {
    try {
      const { data } = await adminService.updateReorderFilter(payload)
      return data
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },

  async uploadAdminImages ({ commit }, payload) {
    try {
      const { data: { message } } = await adminService.uploadAdminImages(payload)
      return message
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },

  async getAdminImagery ({ commit }, params) {
    try {
      const { data: response } = await adminService.getAdminImagery(params)
      commit('GET_ADMIN_IMAGERY', response?.data)
      return response
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },

  async getAdminManagementImagery ({ commit }, params) {
    try {
      const { data: response } = await adminService.getAdminManagementImagery(params)
      commit('GET_ADMIN_MANAGEMENT_IMAGERY', response?.data)
      return response
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async preloadManagementImagery ({ commit, getters }, { params, cacheKey }) {
    try {
      // Check if already cached
      if (getters.preloadCache[cacheKey]) {
        return getters.preloadCache[cacheKey]
      }
      
      const { data: response } = await adminService.getAdminManagementImagery(params)
      
      // Store in cache
      commit('SET_PRELOAD_CACHE', { cacheKey, data: response })
      commit('CLEANUP_PRELOAD_CACHE', 5)
      
      return response
    } catch (error) {
      console.warn('Error preloading imagery:', error)
      return null
    }
  },
  async adminImageryDelete ({ commit }, payload) {
    try {
      return await adminService.adminImageryDelete(payload)
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async adminImageryManagementUpdate ({ commit }, payload) {
    try {
      const { data: response } = await adminService.adminImageryManagementUpdate(payload)
      // commit('GET_ADMIN_MANAGEMENT_IMAGERY', response?.data?.data)
      return response
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  
  async adminImageryUpdateDesign ({ commit }, payload) {
    try {
      const { data: response } = await adminService.adminImageryUpdateDesign(payload)
      return response
    } catch (error) {
      console.log('Error: ', error)
      throw error
    }
  },
  async fetchProductAttributes({ commit }) {
    try {
        return await adminService.fetchProductAttributes()
    } catch (error) {
        console.log('Error: ', error)
        throw error
    }
  },
  async fetchFutureATSDetails({ commit }, { productId, sizeId }) {
    try {
        return await adminService.fetchFutureATSDetails(productId, sizeId);
    } catch (error) {
        console.log('Error: ', error);
        throw error;
    }
  },
  async exportSelectedProducts ({ commit }, productIds) {
    try {
        return await adminService.exportProducts(productIds)
    } catch (error) {
        console.log('Error: ', error)
        throw error
    }
  }
// mutations
}
